import { type ReactNode } from 'react';
import { ComboBox } from '@/components/ComboBox';

export const EMAIL_RE = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;

/** A titled card whose fields sit in a 2-up grid (matches the New/Edit Client layout). */
export function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="card">
      <h3 className="mb-2 border-b border-border pb-2 text-sm font-semibold uppercase tracking-wide text-primary">{title}</h3>
      <div className="grid grid-cols-1 gap-x-4 gap-y-3 sm:grid-cols-2">{children}</div>
    </div>
  );
}

export function Field({ label, children }: { label: ReactNode; children: ReactNode }) {
  return (
    <div>
      <label className="label">{label}</label>
      {children}
    </div>
  );
}

/** Orange "!" validation marker, shown when `show` is true. */
export function Warn({ show, tip }: { show: boolean; tip: string }) {
  if (!show) return null;
  return <span title={tip} className="ml-1 font-bold text-orange-500">!</span>;
}

/** ComboBox bound to a lookup table by a single display column (stores the string value). */
export function LookupCombo({ data, field, value, onChange, placeholder, disabled }: {
  data?: Record<string, unknown>[];
  field: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  disabled?: boolean;
}) {
  const rows = Array.isArray(data) ? data : [];
  // Mirror the Power Apps combo exactly: DefaultSelectedItems is built straight from the
  // stored value — `If(IsBlank(value), Blank(), Table({<field>: value}))` — so the combo
  // always shows what is stored, even a value not present in the option list (e.g. a
  // Maconomy-imported Tax Signer "Arnold Lessman" vs the option "9393 - Arnold Lessman").
  // The options list (rows) still populates the dropdown for selection.
  const selected = value ? ({ [field]: value } as Record<string, unknown>) : null;
  return (
    <ComboBox
      items={rows}
      value={selected}
      onChange={(r) => onChange(r ? String(r[field] ?? '') : '')}
      getLabel={(r) => String(r[field] ?? '')}
      getKey={(r) => String(r[field] ?? '')}
      placeholder={placeholder}
      disabled={disabled}
    />
  );
}
